import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from 'lucide-react'
import Logo from '@/components/site/logo'
import { usePublicSettings } from '@/hooks/use-public-settings'
import { mainNav, legalNav } from '@/constants/site'

const serviceNav = [
    { href: '/account', label: 'Mijn account' },
    { href: '/account/ontwerpen', label: 'Opgeslagen ontwerpen' },
    { href: '/account/bestellingen', label: 'Status van mijn deur' },
    { href: '/werkwijze#veelgestelde-vragen', label: 'Veelgestelde vragen' }
]

const Footer: FC = (): ReactNode => {
    const company = usePublicSettings()

    const address = [company?.street, [company?.postalCode, company?.city].filter(Boolean).join(' ')].filter(Boolean)

    return (
        <footer className='border-t border-border bg-ink text-ink-foreground'>
            <div className='mx-auto max-w-[1440px] px-5 pt-20 pb-10 sm:px-8 lg:px-12'>
                <div className='grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]'>
                    <div className='max-w-sm'>
                        <Logo tone='light' />

                        <p className='mt-6 text-sm leading-relaxed text-ink-foreground/65'>
                            Stalen binnendeuren met glas, op maat ontworpen en vakkundig geplaatst. Van eerste schets tot de laatste schroef, alles in één hand.
                        </p>

                        <Link
                            href='/configurator'
                            className='mt-8 inline-flex items-center gap-2 border-b border-brass pb-1 text-sm font-medium text-ink-foreground transition-colors hover:text-brass'
                        >
                            Start de 3D-configurator
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>

                    <div>
                        <h2 className='text-xs font-semibold tracking-[0.2em] text-brass uppercase'>
                            Ontdekken
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {mainNav.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className='text-sm text-ink-foreground/70 transition-colors hover:text-ink-foreground'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className='text-xs font-semibold tracking-[0.2em] text-brass uppercase'>
                            Klantenservice
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {serviceNav.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className='text-sm text-ink-foreground/70 transition-colors hover:text-ink-foreground'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className='text-xs font-semibold tracking-[0.2em] text-brass uppercase'>
                            Contact
                        </h2>

                        <ul className='mt-5 space-y-4 text-sm text-ink-foreground/70'>
                            {company?.phone && (
                                <li className='flex items-start gap-3'>
                                    <Phone className='mt-0.5 size-4 text-brass' aria-hidden='true' />
                                    <a href={`tel:${company.phone.replace(/[^+\d]/g, '')}`} className='transition-colors hover:text-ink-foreground'>
                                        {company.phone}
                                    </a>
                                </li>
                            )}

                            {company?.email && (
                                <li className='flex items-start gap-3'>
                                    <Mail className='mt-0.5 size-4 text-brass' aria-hidden='true' />
                                    <a href={`mailto:${company.email}`} className='break-all transition-colors hover:text-ink-foreground'>
                                        {company.email}
                                    </a>
                                </li>
                            )}

                            {address.length > 0 && (
                                <li className='flex items-start gap-3'>
                                    <MapPin className='mt-0.5 size-4 text-brass' aria-hidden='true' />
                                    <span>
                                        {address.map(line => (
                                            <span key={line} className='block'>
                                                {line}
                                            </span>
                                        ))}
                                    </span>
                                </li>
                            )}

                            {company?.openingHours && (
                                <li className='flex items-start gap-3'>
                                    <Clock className='mt-0.5 size-4 text-brass' aria-hidden='true' />
                                    <span className='whitespace-pre-line'>
                                        {company.openingHours}
                                    </span>
                                </li>
                            )}

                            <li>
                                <Link href='/contact' className='inline-flex items-center gap-2 text-ink-foreground transition-colors hover:text-brass'>
                                    Stuur ons een bericht
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-16 h-px bg-ink-foreground/10' />

                <div className='mt-8 flex flex-col gap-4 text-xs text-ink-foreground/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p suppressHydrationWarning>
                        &copy; {new Date().getFullYear()} ArtDoors. Alle rechten voorbehouden.
                    </p>

                    <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
                        {legalNav.map(item => (
                            <Link key={item.href} href={item.href} className='transition-colors hover:text-ink-foreground'>
                                {item.label}
                            </Link>
                        ))}

                        <Link href='/backoffice' className='transition-colors hover:text-ink-foreground'>
                            Backoffice
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default Footer
